import React, { useState, useCallback, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import { Person, Business, LocationOn } from '@mui/icons-material';
import npiRegistryService from '../../../services/npiRegistryService';
import { getAuthToken } from '../../../utils/authUtils';


// Simple debounce implementation
const debounce = (func: Function, delay: number) => {
  let timeoutId: number | undefined;
  return (...args: any[]) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => func.apply(null, args), delay);
  };
};

interface NPIProvider {
  number: string;
  enumeration_type: string;
  basic: {
    name?: string;
    first_name?: string;
    last_name?: string;
    credential?: string;
    gender?: string;
  };
  addresses?: Array<{
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    telephone_number?: string;
    address_purpose?: string;
  }>;
  taxonomies?: Array<{
    desc?: string;
    primary?: boolean;
  }>;
}

interface NPIProviderSearchFieldProps {
  value: NPIProvider | null;
  onChange: (provider: NPIProvider | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  sx?: any;
}

export const NPIProviderSearchField: React.FC<NPIProviderSearchFieldProps> = ({
  value,
  onChange,
  label = "External Provider/Facility",
  placeholder = "Search by provider name, organization, or NPI number...",
  disabled = false,
  fullWidth = true,
  sx = {},
}) => {
  const [options, setOptions] = useState<NPIProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Search function without debounce
  const performSearch = useCallback(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let results: NPIProvider[] = [];
      const idToken = await getAuthToken();
      if (!idToken) throw new Error('User not authenticated');

      // Check if input looks like an NPI number (10 digits)
      if (/^\d{10}$/.test(searchTerm)) {
        const provider = await npiRegistryService.getProviderByNPI(idToken, searchTerm);
        results = provider ? [provider] : [];
      } else {
        // Split search term to handle "First Last" format
        const terms = searchTerm.trim().split(/\s+/);
        
        if (terms.length >= 2) {
          // Assume first term is first name, rest is last name for individual providers
          const firstName = terms[0];
          const lastName = terms.slice(1).join(' ');
          
          // Search both individual providers and organizations
          const [individualResults, orgResults] = await Promise.all([
            npiRegistryService.searchProvidersByName(idToken, firstName, lastName),
            npiRegistryService.searchProvidersByName(idToken, undefined, undefined, searchTerm)
          ]);
          
          results = [...individualResults, ...orgResults];
        } else {
          // Single term - search organization names and last names
          const [orgResults, lastNameResults] = await Promise.all([
            npiRegistryService.searchProvidersByName(idToken, undefined, undefined, searchTerm),
            npiRegistryService.searchProvidersByName(idToken, undefined, searchTerm)
          ]);
          
          results = [...orgResults, ...lastNameResults];
        }
      }

      // Remove duplicates based on NPI number
      const uniqueResults = results.filter((provider, index, self) => 
        index === self.findIndex(p => p.number === provider.number)
      );

      setOptions(uniqueResults.slice(0, 20)); // Limit to 20 results
    } catch (err: any) {
      console.error('NPI search error:', err);
      setError(err.message || 'Error searching providers');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce(performSearch, 500),
    [performSearch]
  );

  // Handle input change
  const handleInputChange = (event: any, newInputValue: string) => {
    setInputValue(newInputValue);
    debouncedSearch(newInputValue);
  };

  // Format provider display name
  const getProviderDisplayName = (provider: NPIProvider): string => {
    if (provider.enumeration_type === 'NPI-1') {
      // Individual provider
      const { first_name, last_name, credential } = provider.basic;
      let name = '';
      
      if (first_name) name += first_name;
      if (last_name) name += (name ? ' ' : '') + last_name;
      if (credential) name += `, ${credential}`;
      
      return name || 'Unknown Provider';
    } else {
      // Organization provider
      return provider.basic.name || 'Unknown Organization';
    }
  };

  // Get provider's primary address
  const getPrimaryAddress = (provider: NPIProvider): string => {
    const addresses = provider.addresses || [];
    const primaryAddress = addresses.find(addr => addr.address_purpose === 'LOCATION') || addresses[0];
    
    if (!primaryAddress) return 'No address available';
    
    const parts = [];
    if (primaryAddress.city) parts.push(primaryAddress.city);
    if (primaryAddress.state) parts.push(primaryAddress.state);
    
    return parts.join(', ') || 'Address not available';
  };

  // Get provider's primary specialty
  const getPrimarySpecialty = (provider: NPIProvider): string => {
    const taxonomies = provider.taxonomies || [];
    const primary = taxonomies.find(tax => tax.primary) || taxonomies[0];
    return primary?.desc || '';
  };

  return (
    <Box sx={sx}>
      <Autocomplete
        fullWidth={fullWidth}
        value={value}
        onChange={(event, newValue) => onChange(newValue)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return getProviderDisplayName(option);
        }}
        isOptionEqualToValue={(option, value) => option.number === value.number}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          const displayName = getProviderDisplayName(option);
          const address = getPrimaryAddress(option);
          const specialty = getPrimarySpecialty(option);

          return (
            <li key={key} {...otherProps}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', py: 1 }}>
                <Box sx={{ mr: 2, mt: 0.5 }}>
                  {option.enumeration_type === 'NPI-1' ? (
                    <Person color="primary" fontSize="small" />
                  ) : (
                    <Business color="secondary" fontSize="small" />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    NPI: {option.number} | {option.enumeration_type === 'NPI-1' ? 'Individual' : 'Organization'}
                  </Typography>
                  {specialty && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                      {specialty}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>
                    <LocationOn sx={{ fontSize: 12, mr: 0.5 }} />
                    {address}
                  </Typography>
                </Box>
              </Box>
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                variant="outlined"
                label={getProviderDisplayName(option)}
                {...tagProps}
                disabled={disabled}
              />
            );
          })
        }
        noOptionsText={
          inputValue.length < 2 ? 
            "Type at least 2 characters to search..." : 
            loading ? 
              "Searching..." : 
              "No providers found"
        }
        filterOptions={(x) => x} // We handle filtering server-side
      />

      {value && (
        <Paper sx={{ mt: 1, p: 2, backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Provider:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Name:</strong> {getProviderDisplayName(value)}
              </Typography>
              <Typography variant="body2">
                <strong>NPI:</strong> {value.number}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {value.enumeration_type === 'NPI-1' ? 'Individual Provider' : 'Organization'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {getPrimarySpecialty(value) && (
                <Typography variant="body2">
                  <strong>Specialty:</strong> {getPrimarySpecialty(value)}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Location:</strong> {getPrimaryAddress(value)}
              </Typography>
              {value.addresses?.[0]?.telephone_number && (
                <Typography variant="body2">
                  <strong>Phone:</strong> {value.addresses[0].telephone_number}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}; 